import { createEffect, createMemo } from 'solid-js';
import { getLogger } from '@trimble/logging';
import { CapabilityCoordinatorBase, CapabilityRegistry } from '@trimble/web3d';
import { ProjectDataState } from '../../data-state/DataStateModels';

import {
  SectionMaterialConfig,
  TrajectoryMetadataProvider,
} from '@trimble/web3d-providers';
import {
  getLoadedSectionsByPath,
  getSectionMaterialsByPath,
} from '../../data-state/DataContextProviderHelpers';

export class PathSectionsCoordinator extends CapabilityCoordinatorBase<CapabilityRegistry> {
  constructor(
    capabilityRegistry: CapabilityRegistry,
    protected readonly dataState: ProjectDataState
  ) {
    super(capabilityRegistry);

    const loadedSectionsByPath = createMemo(() =>
      getLoadedSectionsByPath(this.dataState)
    );
    const sectionMaterialsByPath = createMemo(() =>
      getSectionMaterialsByPath(this.dataState)
    );
    createEffect(() =>
      this.syncSections(sectionMaterialsByPath(), loadedSectionsByPath())
    );
  }

  public updateMissionPathSections(): void {
    this.syncSections(
      getSectionMaterialsByPath(this.dataState),
      getLoadedSectionsByPath(this.dataState)
    );
  }

  private syncSections(
    materialsByPath: Map<string, SectionMaterialConfig[]>,
    loadedByPath: Map<string, string[]>
  ): void {
    const trajectoryMetadataProviders = this.registry.getPaneCapabilities(
      TrajectoryMetadataProvider
    );

    if (
      !trajectoryMetadataProviders ||
      trajectoryMetadataProviders.length === 0
    ) {
      return;
    }

    for (const trajectoryMetadataProvider of trajectoryMetadataProviders) {
      for (const [pathId, materials] of materialsByPath) {
        trajectoryMetadataProvider
          .updateSectionMaterials(pathId, materials)
          .catch((error) =>
            getLogger().error(
              `Failed to update section materials for ${pathId}`,
              error
            )
          );
      }
      for (const [pathId, loadedSections] of loadedByPath) {
        trajectoryMetadataProvider
          .updateLoadedSections(pathId, loadedSections)
          .catch((error) =>
            getLogger().error(
              `Failed to update loaded sections for ${pathId}`,
              error
            )
          );
      }
    }
  }

  public cleanup(): Promise<void> {
    // Effects are automatically cleaned up by SolidJS when the reactive owner disposes
    return Promise.resolve();
  }
}
